/**
 * FinancePage — the Finance dashboard (Phase 19 / Task 19.4, DEC-325; master spec §22).
 *
 * Reads GET /v1/dashboards/finance: the billing pipeline, aging and outstanding by
 * customer, payment terms, invoice metrics against the previous period, revenue by job
 * type and invoiced volume, and the QuickBooks export status.
 *
 * DOLLARS: every VIEW_INVOICES role sees them, billing staff included (DEC-332, Corbin
 * 2026-10-13 — overrides §22's "counts only", consistent with Pipeline/Invoices/Aging).
 *
 * Export: Excel / CSV / Print (Phase 19 / Task 19.5) through DashboardExport — the files
 * are built server-side from this same response.
 *
 * Dates: the period is company-local calendar days; timestamps render in the viewer's
 * zone (DEC-004). VIEW_INVOICES is enforced by the API (three-layer security).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useApiOpts } from '@lodex-pro/web-shared';

import { useUserTimezone } from '../lib/use-user-timezone';
import { formatTimestamp } from '../lib/timestamp';
import {
  useFinanceDashboard,
  type CountCents,
  type FinanceDashboardResponse,
  type PipelineStageKey,
} from '../features/finance/use-finance-dashboard';
import {
  formatBbl,
  formatCents,
  formatChange,
  formatPct,
  formatShortDay,
  plural,
  presetRange,
  type RangePreset,
} from '../features/finance/finance-format';
import { InvoicedVolumeChart, JobTypePie, SLICE_COLORS } from '../features/finance/FinanceCharts';
import { DashboardExport } from '../features/finance/DashboardExport';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** CSV choices — the API's financeTables names; the first is its default. */
const FINANCE_EXPORT_TABLES = [
  { name: 'outstanding', label: 'Outstanding by customer' },
  { name: 'pipeline', label: 'Billing pipeline' },
  { name: 'aging', label: 'Aging' },
  { name: 'summary', label: 'Invoice metrics' },
  { name: 'payment-terms', label: 'Due dates' },
  { name: 'job-types', label: 'Invoiced by job type' },
  { name: 'volume', label: 'Invoiced volume' },
] as const;

/** A whole panel plugged into the page (DEC-325 D-5). Empty until one ships. */
interface FinancePanel {
  readonly id: string;
  readonly render: (data: FinanceDashboardResponse) => React.ReactNode;
}

/** Further Finance panels plug in here. Payment terms and accounting export are built in. */
const FINANCE_PANELS: readonly FinancePanel[] = [];

const STAGES: Record<PipelineStageKey, { label: string; hint: string; to: string | null }> = {
  COMPLETED: {
    label: 'Completed, awaiting review or re-capture',
    hint: 'Sent back for re-capture, or never entered review',
    to: null,
  },
  IN_REVIEW: { label: 'In review', hint: 'Waiting on a reviewer in dispatch', to: null },
  TO_BE_INVOICED: { label: 'Ready to invoice', hint: 'Open the Pipeline', to: '/' },
  INVOICED: { label: 'Invoiced, awaiting payment', hint: 'Open Invoices', to: '/invoices' },
};

const PRESETS: readonly { id: RangePreset; label: string }[] = [
  { id: 'MONTH_TO_DATE', label: 'This month' },
  { id: 'LAST_30', label: 'Last 30 days' },
  { id: 'LAST_90', label: 'Last 90 days' },
];

const sectionStyle: React.CSSProperties = {
  borderRadius: 'var(--radius-card)',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-secondary)',
};
const sectionTitle: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: '16px',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};
const mutedStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const bigNumber: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  borderBottom: '1px solid var(--color-border-secondary)',
};
const numTh: React.CSSProperties = { ...thStyle, textAlign: 'right' };
const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  borderBottom: '1px solid var(--color-border-secondary)',
};
const numTd: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '16px',
};

function Kpi({
  label,
  value,
  sub,
  change,
  previous,
  testId,
}: {
  label: string;
  value: string;
  sub?: string | undefined;
  change?: number | null | undefined;
  previous?: string | undefined;
  testId: string;
}): React.JSX.Element {
  const c = formatChange(change);
  const color =
    c.direction === 'UP'
      ? 'var(--color-success)'
      : c.direction === 'DOWN'
        ? 'var(--color-danger)'
        : 'var(--color-text-tertiary)';
  return (
    <div data-testid={testId} style={{ ...sectionStyle, flex: '1 1 180px', minWidth: '180px' }}>
      <div style={mutedStyle}>{label}</div>
      <div style={bigNumber}>{value}</div>
      {sub !== undefined && <div style={{ fontSize: '13px' }}>{sub}</div>}
      {previous !== undefined && (
        <div style={{ fontSize: '12px' }}>
          <span style={{ color, fontWeight: 600 }}>{c.text}</span>{' '}
          <span style={mutedStyle}>vs {previous}</span>
        </div>
      )}
    </div>
  );
}

function Pipeline({ data }: { data: FinanceDashboardResponse }): React.JSX.Element {
  return (
    <section style={sectionStyle} aria-label="Billing pipeline" data-testid="finance-pipeline">
      <h2 style={sectionTitle}>Billing pipeline (right now)</h2>
      <ol
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: '8px',
        }}
      >
        {data.pipeline.map((s, i) => {
          const meta = STAGES[s.key];
          const body = (
            <>
              <div style={mutedStyle}>
                {i + 1}. {meta.label}
              </div>
              <div style={bigNumber}>{plural(s.jobs, 'job')}</div>
              {s.cents !== null && (
                <div style={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCents(s.cents)}
                </div>
              )}
              <div style={mutedStyle}>
                {s.sentBack === null
                  ? meta.hint
                  : `${String(s.sentBack)} sent back for re-capture · ` +
                    `${String(s.jobs - s.sentBack)} never entered review`}
              </div>
            </>
          );
          const boxStyle: React.CSSProperties = {
            display: 'block',
            borderRadius: '12px',
            backgroundColor: 'var(--color-bg-tertiary)',
            padding: '10px 12px',
            color: 'inherit',
            textDecoration: 'none',
            minHeight: '56px',
          };
          return (
            <li key={s.key} data-testid={`stage-${s.key}`}>
              {meta.to === null ? (
                <div style={boxStyle}>{body}</div>
              ) : (
                <Link to={meta.to} style={boxStyle}>
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const BUCKETS: readonly { key: 'current' | 'd31_60' | 'd61_90' | 'd90plus'; label: string }[] = [
  { key: 'current', label: '0–30 days' },
  { key: 'd31_60', label: '31–60 days' },
  { key: 'd61_90', label: '61–90 days' },
  { key: 'd90plus', label: '90+ days' },
];

function sumBuckets(a: CountCents, b: CountCents, c: CountCents): CountCents {
  return { count: a.count + b.count + c.count, cents: a.cents + b.cents + c.cents };
}

function Aging({ data }: { data: FinanceDashboardResponse }): React.JSX.Element {
  const older = sumBuckets(data.aging.d31_60, data.aging.d61_90, data.aging.d90plus);
  return (
    <section style={sectionStyle} aria-label="Aging" data-testid="finance-aging">
      <h2 style={sectionTitle}>Unpaid invoices by age</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Age</th>
            <th style={numTh}>Invoices</th>
            <th style={numTh}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {BUCKETS.map((b) => (
            <tr key={b.key}>
              <td style={tdStyle}>{b.label}</td>
              <td style={numTd}>{data.aging[b.key].count}</td>
              <td style={numTd}>{formatCents(data.aging[b.key].cents)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...tdStyle, fontWeight: 700 }}>Total unpaid</td>
            <td style={{ ...numTd, fontWeight: 700 }}>{formatNumber(data.aging.total.count)}</td>
            <td style={{ ...numTd, fontWeight: 700 }}>{formatCents(data.aging.total.cents)}</td>
          </tr>
        </tbody>
      </table>
      <p style={{ fontSize: '13px', margin: '10px 0 0' }} data-testid="older-than-30">
        Unpaid more than 30 days: <strong>{plural(older.count, 'invoice')}</strong>
        {` · ${formatCents(older.cents)}`}
      </p>
      <p style={{ ...mutedStyle, margin: '6px 0 0' }}>
        Aged by your local day ({data.viewerTimezone}), the same as the{' '}
        <Link to="/aging">Aging report</Link>.
      </p>
    </section>
  );
}

function Outstanding({ data }: { data: FinanceDashboardResponse }): React.JSX.Element {
  return (
    <section style={sectionStyle} aria-label="Outstanding by customer">
      <h2 style={sectionTitle}>Outstanding by customer</h2>
      {data.outstanding.length === 0 ? (
        <p style={mutedStyle}>Nothing outstanding.</p>
      ) : (
        <table
          style={{ width: '100%', borderCollapse: 'collapse' }}
          data-testid="finance-outstanding"
        >
          <thead>
            <tr>
              <th style={thStyle}>Customer</th>
              <th style={numTh}>Invoices</th>
              <th style={numTh}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.outstanding.map((c) => (
              <tr key={`${c.haulerOperatorId ?? ''}:${c.operatorId ?? ''}:${c.name}`}>
                <td style={tdStyle}>{c.name}</td>
                <td style={numTd}>{c.invoices}</td>
                <td style={numTd}>{formatCents(c.cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PaymentTerms({ data }: { data: FinanceDashboardResponse }): React.JSX.Element {
  const t = data.paymentTerms;
  const line = (label: string, m: { invoiceCount: number; totalCents: number }): string =>
    `${label}: ${plural(m.invoiceCount, 'invoice')} · ${formatCents(m.totalCents)}`;
  return (
    <section style={sectionStyle} aria-label="Payment terms" data-testid="finance-payment-terms">
      <h2 style={sectionTitle}>Due dates</h2>
      <p style={{ fontSize: '14px', margin: '0 0 6px', fontWeight: 600 }}>
        {line('Past due', t.overdue)}
      </p>
      <p style={{ fontSize: '13px', margin: '0 0 6px' }}>
        {line('Due in the next 7 days', t.dueNext7Days)}
      </p>
      <p style={{ fontSize: '13px', margin: '0 0 6px' }}>
        Paid on time this period: <strong>{formatPct(t.paidInRange.paidOnTimePct)}</strong>{' '}
        <span style={mutedStyle}>
          ({formatNumber(t.paidInRange.paidOnTimeCount)} of{' '}
          {formatNumber(t.paidInRange.invoiceCount)})
        </span>
      </p>
      {t.withoutDueDate.invoiceCount > 0 && (
        <p style={mutedStyle}>
          {plural(t.withoutDueDate.invoiceCount, 'unpaid invoice')} sent before payment terms have
          no due date.
        </p>
      )}
    </section>
  );
}

function AccountingExport({ data }: { data: FinanceDashboardResponse }): React.JSX.Element {
  const a = data.accountingExport;
  return (
    <section style={sectionStyle} aria-label="QuickBooks export" data-testid="finance-export">
      <h2 style={sectionTitle}>QuickBooks export</h2>
      <p style={{ fontSize: '13px', margin: '0 0 6px' }}>
        {a.lastExportAt === null
          ? 'No export recorded yet.'
          : `Last export ${formatTimestamp(a.lastExportAt, data.viewerTimezone)}${
              a.lastExportInvoices === null ? '' : ` · ${plural(a.lastExportInvoices, 'invoice')}`
            }`}
      </p>
      <p style={{ fontSize: '13px', margin: 0 }}>
        Not yet exported: <strong>{plural(a.notYetExported, 'invoice')}</strong>
        {` · ${formatCents(a.notYetExportedCents)}`}
      </p>
      <p style={{ ...mutedStyle, margin: '6px 0 0' }}>
        {a.cadence === 'SCHEDULED' ? 'Sent on a schedule.' : 'Downloaded on demand.'}
      </p>
    </section>
  );
}

function JobTypes({ data }: { data: FinanceDashboardResponse }): React.JSX.Element {
  const rows = data.revenueByJobType;
  return (
    <section style={sectionStyle} aria-label="Revenue by job type">
      <h2 style={sectionTitle}>Invoiced by job type</h2>
      {rows.length === 0 ? (
        <p style={mutedStyle}>No invoices sent in this period.</p>
      ) : (
        <>
          <JobTypePie rows={rows} />
          <table
            style={{ width: '100%', borderCollapse: 'collapse' }}
            data-testid="finance-job-types"
          >
            <thead>
              <tr>
                <th style={thStyle}>Job type</th>
                <th style={numTh}>Jobs</th>
                <th style={numTh}>Amount (before tax)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${String(r.jobTypeId)}:${r.label}`}>
                  <td style={tdStyle}>
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        marginRight: '6px',
                        backgroundColor: SLICE_COLORS[i % SLICE_COLORS.length],
                      }}
                    />
                    {r.label}
                  </td>
                  <td style={numTd}>{r.jobs}</td>
                  <td style={numTd}>{formatCents(r.cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

export function FinancePage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const timeZone = useUserTimezone();
  const [preset, setPreset] = React.useState<RangePreset | 'CUSTOM'>('LAST_30');
  const [range, setRange] = React.useState(() => presetRange('LAST_30', timeZone));
  const { data, isLoading, isError } = useFinanceDashboard(apiOpts, range.from, range.to);

  const setCustom = (key: 'from' | 'to', value: string): void => {
    if (value === '') return;
    setPreset('CUSTOM');
    setRange((r) => ({ ...r, [key]: value }));
  };

  const prevLabel =
    data === undefined
      ? ''
      : `${formatShortDay(data.previous.from)} – ${formatShortDay(data.previous.to)}`;

  return (
    <div
      data-print-root
      style={{
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '1280px',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={preset === p.id}
            onClick={(): void => {
              setPreset(p.id);
              setRange(presetRange(p.id, timeZone));
            }}
            style={{
              minHeight: '40px',
              padding: '0 14px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-primary)',
              backgroundColor:
                preset === p.id ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
              color: 'var(--color-text-primary)',
              fontWeight: preset === p.id ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
        <label style={mutedStyle}>
          From{' '}
          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e): void => setCustom('from', e.target.value)}
          />
        </label>
        <label style={mutedStyle}>
          To{' '}
          <input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e): void => setCustom('to', e.target.value)}
          />
        </label>
      </div>
      <DashboardExport
        path="/v1/dashboards/finance"
        params={{ from: range.from, to: range.to }}
        slug="finance"
        tables={FINANCE_EXPORT_TABLES}
      />

      {isLoading && <p role="status">Loading finance dashboard…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Couldn&apos;t load the finance dashboard. Refresh to try again.
        </p>
      )}

      {data !== undefined && (
        <>
          <Pipeline data={data} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Kpi
              testId="kpi-sent"
              label="Invoices sent"
              value={formatCents(data.current.sentCents)}
              sub={plural(data.current.sent, 'invoice')}
              change={data.changes.sentCentsPct}
              previous={prevLabel}
            />
            <Kpi
              testId="kpi-paid"
              label="Invoices paid"
              value={formatCents(data.current.paidCents)}
              sub={plural(data.current.paid, 'invoice')}
              change={data.changes.paidCentsPct}
              previous={prevLabel}
            />
            <Kpi
              testId="kpi-days-to-pay"
              label="Average days to pay"
              value={
                data.current.avgDaysToPay === null
                  ? '—'
                  : `${formatNumber(data.current.avgDaysToPay, { decimals: 1 })} days`
              }
              sub={
                data.previous.avgDaysToPay === null
                  ? undefined
                  : `${formatNumber(data.previous.avgDaysToPay, { decimals: 1 })} days in ${prevLabel}`
              }
            />
            <Kpi
              testId="kpi-invoiced-volume"
              label="Invoiced volume"
              value={`${formatBbl(data.current.invoicedBbl)} BBL`}
              sub={plural(data.current.invoicedLoads, 'load')}
              change={data.changes.invoicedBblPct}
              previous={prevLabel}
            />
          </div>

          <div style={grid}>
            <Aging data={data} />
            <Outstanding data={data} />
            <PaymentTerms data={data} />
            <AccountingExport data={data} />
            <JobTypes data={data} />
            <section style={sectionStyle} aria-label="Invoiced volume">
              <h2 style={sectionTitle}>Invoiced volume by day sent</h2>
              <InvoicedVolumeChart points={data.invoicedVolume} />
              <p style={mutedStyle}>Company days ({data.companyTimezone}).</p>
            </section>
            {FINANCE_PANELS.map((p) => (
              <React.Fragment key={p.id}>{p.render(data)}</React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
